import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Breadcrumbs, Button, PageHeader, Select, TextInput, Textarea } from "@opendate-app/setlist";
import client from "../../../shared/api/client";
import { toastError } from "../../../shared/toastHelper";
import { SCOPE_TEAM, SCOPE_VENUES, formatCents } from "../../GiftCardProducts/giftCardFormat";
import { currencySymbol, toCents } from "../giftCardLabels";
import "../styles/giftCards.css";

const NEVER_EXPIRES = "never";

const scopeOptions = [
  { value: SCOPE_TEAM, label: "All team venues" },
  { value: SCOPE_VENUES, label: "Selected venues" },
];

const expiryLabel = (choice) => (choice === NEVER_EXPIRES ? "Never" : `${choice} months after issue`);

const ComplimentaryGiftCardForm = ({ createPath, cardsPath, currency, venueOwnerships, expiryChoices }) => {
  const [form, setForm] = useState({
    recipientName: "",
    recipientEmail: "",
    amount: "",
    scope: SCOPE_TEAM,
    venueOwnershipIds: [],
    expiresIn: expiryChoices[0],
    message: "",
    note: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const update = (changes) => setForm((current) => ({ ...current, ...changes }));
  const amountCents = toCents(form.amount);
  const expiryOptions = useMemo(
    () => expiryChoices.map((choice) => ({ value: choice, label: expiryLabel(choice) })),
    [expiryChoices]
  );
  const ready =
    form.recipientEmail.trim().length > 0 &&
    amountCents > 0 &&
    form.note.trim().length > 0 &&
    (form.scope !== SCOPE_VENUES || form.venueOwnershipIds.length > 0) &&
    !submitting;

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await client.post(createPath, {
        note: form.note,
        expires_in: form.expiresIn,
        gift_card: {
          recipient_name: form.recipientName,
          recipient_email: form.recipientEmail.trim(),
          initial_value_cents: amountCents,
          scope: form.scope,
          venue_ownership_ids: form.scope === SCOPE_VENUES ? form.venueOwnershipIds : [],
          message: form.message,
        },
      });
      window.location.href = data.path;
    } catch (error) {
      const errors = error?.response?.data?.errors;
      toastError(errors ? errors.join(", ") : "Couldn't issue the card. Please try again.");
      setSubmitting(false);
    }
  };

  return (
    <div className="gift-cards">
      <PageHeader
        breadcrumbs={<Breadcrumbs items={[{ label: "Gift cards", href: cardsPath }, { label: "Issue free cards" }]} />}
        title="Issue free cards"
        supportingText="Comp a fan, thank a sponsor, or make good on a cancelled show."
      />
      <div className="gift-cards-panel complimentary-gift-card-form">
        <div className="complimentary-gift-card-grid">
          <TextInput
            id="complimentary-gift-card-name"
            label="Recipient name"
            value={form.recipientName}
            onChange={(event) => update({ recipientName: event.target.value })}
          />
          <TextInput
            id="complimentary-gift-card-email"
            label="Recipient email"
            type="email"
            value={form.recipientEmail}
            onChange={(event) => update({ recipientEmail: event.target.value })}
          />
          <TextInput
            id="complimentary-gift-card-amount"
            label="Amount"
            inputMode="decimal"
            leadingText={currencySymbol(currency)}
            placeholder="0.00"
            value={form.amount}
            onChange={(event) => update({ amount: event.target.value })}
          />
          <Select
            id="complimentary-gift-card-scope"
            label="Works at"
            options={scopeOptions}
            value={form.scope}
            onChange={(value) => update({ scope: value })}
          />
          <Select
            id="complimentary-gift-card-expiry"
            label="Expires"
            options={expiryOptions}
            value={form.expiresIn}
            onChange={(value) => update({ expiresIn: value })}
            hint="Free cards can expire sooner than paid ones."
          />
        </div>
        {form.scope === SCOPE_VENUES && (
          <Select
            id="complimentary-gift-card-venues"
            label="Venues"
            multiple
            searchable
            placeholder="Add venues"
            options={venueOwnerships}
            value={form.venueOwnershipIds}
            onChange={(value) => update({ venueOwnershipIds: value || [] })}
          />
        )}
        <Textarea
          id="complimentary-gift-card-message"
          label="Message (optional)"
          rows={3}
          value={form.message}
          onChange={(event) => update({ message: event.target.value })}
        />
        <TextInput
          id="complimentary-gift-card-note"
          label="Internal note (required, team only)"
          value={form.note}
          onChange={(event) => update({ note: event.target.value })}
        />
        <div className="gift-card-modal-footer">
          <Button variant="secondary" onClick={() => (window.location.href = cardsPath)} disabled={submitting}>
            Cancel
          </Button>
          <Button variant="primary" disabled={!ready} onClick={submit}>
            {`Issue and email ${formatCents(amountCents, currency)} card`}
          </Button>
        </div>
      </div>
    </div>
  );
};

ComplimentaryGiftCardForm.propTypes = {
  createPath: PropTypes.string.isRequired,
  cardsPath: PropTypes.string.isRequired,
  currency: PropTypes.string.isRequired,
  venueOwnerships: PropTypes.arrayOf(PropTypes.shape({ value: PropTypes.number, label: PropTypes.string })).isRequired,
  expiryChoices: PropTypes.arrayOf(PropTypes.string).isRequired,
};

export default ComplimentaryGiftCardForm;
